/**
 * A11Y — accessibility report row 10 (and the same dialog checklist repeated in
 * rows 7 and in the "קטגוריה 1" tab).
 *
 * Two gaps that the theme's own dialog system does not cover:
 *
 *   1. aria-labelledby. The theme's DialogElement already sets role="dialog",
 *      traps focus, closes on Escape and returns focus to the trigger (via the
 *      focus-trap library in vendor.min.js). What it never does is give the
 *      dialog an accessible NAME: the title is passed in as <… slot="header">
 *      light-DOM content and nothing links the dialog to it. So a screen reader
 *      announces "dialog" with no indication of which dialog. This script gives
 *      the slotted title an id and points the dialog's aria-labelledby at it.
 *
 *   2. aria-modal. Only the Modal class sets it, so Drawer and CartDrawer
 *      inherit it — but Popover and HeaderSearch extend DialogElement directly
 *      and never get it, even though DialogElement does trap focus for them.
 *      The size-picker popover is one of these, which is why the same note
 *      shows up again on the category-page tab.
 *
 * Done here rather than in assets/theme.js on purpose: theme.js is a compiled
 * bundle and editing it would be overwritten by the next theme update.
 *
 * Purely additive — sets attributes only, never moves or restyles anything.
 */
(function () {
  'use strict';

  var seq = 0;

  function nameDialog(dialog) {
    if (!dialog || dialog.getAttribute('aria-labelledby')) return;

    // The title is always light-DOM content assigned to the header slot.
    var title = dialog.querySelector(':scope > [slot="header"]');
    if (!title) return;

    if (!title.id) {
      seq += 1;
      title.id = 'dialog-title-' + seq + (dialog.id ? '-' + dialog.id : '');
    }

    dialog.setAttribute('aria-labelledby', title.id);
  }

  function markModal(dialog) {
    // DialogElement activates a focus trap for every dialog it manages, so
    // aria-modal is an accurate statement for popovers and the header search too.
    var tag = dialog.tagName.toLowerCase();
    if (tag !== 'x-popover' && tag !== 'header-search') return;
    if (!dialog.hasAttribute('aria-modal')) {
      dialog.setAttribute('aria-modal', 'true');
    }
  }

  function wire(dialog) {
    nameDialog(dialog);
    markModal(dialog);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('[role="dialog"]'), wire);
  }

  scan();
  document.addEventListener('DOMContentLoaded', function () {
    scan();
  });

  // Dialogs get role="dialog" in connectedCallback, are moved to <body> when they
  // open, and whole sections are replaced on AJAX cart/variant updates — so watch
  // for both new nodes and the role attribute appearing.
  if (typeof MutationObserver === 'function') {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes') {
          if (mutation.target.getAttribute('role') === 'dialog') wire(mutation.target);
          return;
        }

        Array.prototype.forEach.call(mutation.addedNodes || [], function (node) {
          if (node.nodeType !== 1) return;
          if (node.getAttribute && node.getAttribute('role') === 'dialog') wire(node);
          scan(node);
        });
      });
    }).observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['role']
    });
  }
})();
